import { Card, BenefitCard, FAQ } from 'sections/DigitalTools/ShipSchedules/types';

export const cards: Card[] = [
  {
    id: 1,
    title: {
      firstLine: '160+',
      secondLine: 'Global Carriers',
    },
    description: 'Search shipping schedules from all significant and specialised service providers, including deep-sea, short-sea, and feeder services.',
  },
  {
    id: 2,
    title: {
      firstLine: '15+',
      secondLine: 'Million Schedules',
    },
    description: 'Stay steps ahead with real-time access to a powerful pool of present and past sailing schedules.',
  },
  {
    id: 3,
    title: {
      firstLine: '95,000+',
      secondLine: 'Active Voyages Tracked',
    },
    description: 'Plan smarter shipping paths with simple access to sailing progress, ship performance, and port-to-port patterns.',
  },
  {
    id: 4,
    title: {
      firstLine: '6,000+ Ports and 180,000+',
      secondLine: 'Port Pairs',
    },
    description: 'Plan seamless shipping solutions from start to port with peace of mind.',
  },
  {
    id: 5,
    title: {
      firstLine: '5,000+',
      secondLine: 'Vessels Monitored',
    },
    description: 'Get updates on container ships, cargo vessels, and their estimated times of arrival (ETAs).',
  },
];

export const benefitsCards: BenefitCard[] = [
  {
    id: 1,
    icon: 'icon-ship.svg',
    alt: 'Vessel Tracking icon',
    title: {
      firstLine: 'Live',
      secondLine: 'Vessel Tracking',
    },
    description: (
      <>
        Stay in sync with your shipments using <a href="https://www.searates.com/schedules/?searchType=by_points">SeaRates’ vessel schedule tracking system</a>, which pairs satellite-supported AIS tracking with carrier-provided container ship schedules.
      </>
    ),
  },
  {
    id: 2,
    icon: 'icon-pen.svg',
    alt: 'Smart Planning & Precise Booking icon',
    title: {
      firstLine: 'Smart Planning &',
      secondLine: 'Precise Booking',
    },
    description: 'Search sailing schedules using flexible parameters, by port, provider, or particular vessel, and perfect your planning by booking shipments based on sailing speed, schedule strength, and service performance.',
  },
  {
    id: 3,
    icon: 'icon-pen.svg',
    alt: 'Total Transparency & Reliable Results icon',
    title: {
      firstLine: 'Total Transparency &',
      secondLine: 'Reliable Results',
    },
    description: 'Take the guesswork out of global shipping. Track transit times, review route rotations, and rely on real-time reports and rich historical records to reassure customers and refine your operations.',
  },
  {
    id: 4,
    icon: 'icon-badge-check.svg',
    alt: 'Data-Driven Forecasting icon',
    title: {
      firstLine: 'Data-Driven',
      secondLine: 'Forecasting',
    },
    description: 'Dive into detailed data from millions of documented sailing schedules to forecast freight flows, fine-tune delivery dates, and form better financial plans.',
  },
];

export const solutionCards: Card[] = [
  {
    id: 1,
    title: {
      firstLine: 'Dynamic Data Consolidation',
      secondLine: 'for Directional Voyages',
    },
    description: 'Simplify decisions with direct access to port-to-port vessel schedules. Streamline shipping strategies and support smarter deliveries with structured, reliable data.',
  },
  {
    id: 2,
    title: {
      firstLine: 'Seamless Ship',
      secondLine: 'Voyage Integration',
    },
    description: 'Synchronise sailing schedules seamlessly with your platform. Show shipping lines\' schedules, plot vessel route maps, and present real-time projections of global shipping routes.',
  },
  {
    id: 3,
    title: {
      firstLine: 'Smart',
      secondLine: 'Schedule Finder',
    },
    description: 'Search and scan sailing schedules with smart filters. Support supply chain success using timely vessel arrival schedules and tools for port-to-port tracking.',
  },
  {
    id: 4,
    title: {
      firstLine: 'Data-on-Demand',
      secondLine: 'for Ocean Schedules',
    },
    description: 'Find and fetch full vessel voyage details whenever needed. Fast-track freight planning by identifying cost-effective shipping routes and tracking vessel arrivals at a glance.',
  },
  {
    id: 5,
    title: {
      firstLine: 'Strategic Schedule Search',
      secondLine: 'Optimisation',
    },
    description: 'Solve supply chain struggles with smart schedule search solutions. Discover direct or redirected routes and rely on the best port-to-port vessel schedules.',
  },
  {
    id: 6,
    title: {
      firstLine: 'Actionable Analytics & Voyage',
      secondLine: 'Reporting',
    },
    description: 'See vessel arrival schedules, study global shipping route maps, and streamline shipping timelines to save costs.',
  },
  {
    id: 7,
    title: {
      firstLine: 'Detailed Data in Flexible Formats',
      secondLine: '',
    },
    description: 'Download dependable data on shipping lines\' schedules in CSV or XLS, perfect for faster planning, focused reporting, and fluid logistics integration.',
  },
  {
    id: 8,
    title: {
      firstLine: 'Comprehensive Carrier List & Voyage Tracker',
      secondLine: '',
    },
    description: 'Browse 160+ shipping lines including deep-sea, short-sea, and feeder services. Find freight-friendly routes and see how to track a vessel online with ease.',
  },
];

export const faq: FAQ[] = [
  {
    id: "1",
    question: "How can I track a vessel online?",
    answer: (cn: Function) => (
      `
        <p class='${cn('faq__answer-paragraph')}'>
          Use our powerful platform “Ship Schedules” to track vessels in real time with advanced AIS integration and accurate schedule data. Simply search by vessel name, IMO number, or voyage code to follow fleet movements with speed.
        </p>
      `
    ),
  },
  {
    id: "2",
    question: "How do I find a port-to-port vessel schedule?",
    answer: (cn: Function) => (
      `
        <p class='${cn('faq__answer-paragraph')}'>
          As part of the Shipping Schedules tool, simply select your preferred port of origin and destination in the schedule search bar. Instantly scan sailing schedules, compare carriers, and calculate clear transit times, all in one smart, streamlined solution.
        </p>
      `
    ),
  },
  {
    id: "3",
    question: "What is a vessel schedule and why is it important?",
    answer: (cn: Function) => (
      `
        <p class='${cn('faq__answer-paragraph')}'>
          A vessel schedule is a structured summary of a ship’s sailing route, scheduled stops, and estimated times of arrival. For freight forwarders, it’s a critical coordination tool that keeps cargo moving with precision, punctuality, and planning power.
        </p>
      `
    ),
  },
  {
    id: "4",
    question: "Which shipping lines are included?",
    answer: (cn: Function) => (
      `
        <p class='${cn('faq__answer-paragraph')}'>
          Our Shipping Schedules tool features sailing schedules from over 160 carriers, covering global giants and specialised regional services, so you can compare and choose with confidence.
        </p>
      `
    ),
  },
  {
    id: "5",
    question: "Does the tool show global vessel routes?",
    answer: (cn: Function) => (
      `
        <p class='${cn('faq__answer-paragraph')}'>
          Yes. Our route visualisation tools show you where vessels are right now and the paths they’re taking, so you get a clear and complete view of how your cargo is moving.
        </p>
      `
    ),
  },
];